import React, { memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';

import {
  BarChart2,
  Users,
  Search,
  FileText,
  Send,
  ChevronDown,
  Info,
  List,
} from 'lucide-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';

import { getAdminId, getPermissions, getRoles } from '../../utils/tokenUtils';
import SimpleAlert from '../common/SimpleAlert';

import 'react-perfect-scrollbar/dist/css/styles.css';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  route?: string;
  requiredPermission?: string;
  children?: MenuItem[]; // Sub-items
}

interface MenuGroup {
  group: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = memo(({ isOpen, activePage }) => {
  // Add ref for the PerfectScrollbar container
  const scrollContainerRef = React.useRef<any>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const roles = useMemo(() => getRoles() || [], []);
  const permissions = getPermissions() || [];
  const navigate = useNavigate();
  const adminId = getAdminId();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleNavigation = (page: string): void => {
    navigate(page);
  };

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    );
  };

  const isRouteActive = useCallback(
    (route: string) => {
      // Return false if either path is missing
      if (!activePage || !route) {
        return false;
      }

      const currentPath = activePage.toLowerCase();
      const menuRoute = route.toLowerCase();

      // Check if we're on the exact route
      if (currentPath === menuRoute) {
        return true;
      }

      // Check if we're on a sub-route (detail page, create page, etc.)
      try {
        return currentPath.startsWith(menuRoute + '/');
      } catch {
        // If something goes wrong, fall back to simple comparison
        return false;
      }
    },
    [activePage],
  );

  const renderMenuItems = useCallback((items: MenuItem[]) => {
    return items.map((item, idx) => {
      const isParentActive = item.children
        ? item.children.some(
            (child) => child.route && isRouteActive(child.route),
          )
        : item.route && isRouteActive(item.route);

      const isExpanded = expandedItems.includes(item.label) || isParentActive;

      return (
        <li key={idx}>
          <a
            onClick={() => {
              if (item.children) {
                toggleExpand(item.label);
              } else if (item.route) {
                handleNavigation(item.route);
              }
            }}
            className={`flex items-center gap-3 p-3 rounded transition text-sm cursor-pointer ${
              isParentActive
                ? 'bg-cream text-primary border-l-4 border-secondary dark:bg-primary dark:text-cream'
                : 'hover:bg-cream hover:text-primary text-cream dark:hover:bg-primary dark:hover:text-cream'
            }`}
          >
            {item.icon}
            <span className="flex-1">{item.label}</span>
            {item.children && (
              <ChevronDown
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            )}
          </a>
          {item.children && (
            <ul
              className={`relative ml-6 overflow-hidden transition-all duration-200 ease-in-out ${
                isExpanded ? 'max-h-96 mt-1' : 'max-h-0'
              }`}
            >
              <div className="absolute top-0 bottom-0 w-px bg-seam"></div>
              {item.children.map((child, childIdx) => (
                <li key={`${idx}-${childIdx}`} className="relative">
                  <div className="absolute left-0 top-1/2 w-4 h-px bg-seam"></div>
                  <a
                    onClick={() => child.route && handleNavigation(child.route)}
                    data-route={child.route}
                    className={`flex items-center gap-3 py-2 pl-6 rounded transition text-sm cursor-pointer ${
                      child.route && isRouteActive(child.route)
                        ? 'text-primary'
                        : 'text-harbor hover:text-cream'
                    }`}
                  >
                    <span>{child.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  }, [expandedItems, isRouteActive, handleNavigation]);

  const menuGroups: MenuGroup[] = useMemo(() => [
    ...(roles.includes('Admin')
      ? [
          {
            group: 'Main',
            items: [
              {
                label: 'Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/admin/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
            ],
          },
          {
            group: 'Communication',
            items: [
              {
                label: 'Bulk Email',
                icon: <Send className="w-5 h-5" />,
                route: '/admin/bulkemails',
                requiredPermission: 'Communication.BulkEmails.View',
              },
            ],
          },
          {
            group: 'Member',
            items: [
              {
                label: 'Members',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/members',
                requiredPermission: 'Member.Members.View',
              },
            ],
          },
          {
            group: 'Settings',
            items: [
              {
                label: 'Audit Logs',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/auditlogs',
                requiredPermission: 'Settings.AuditLog.View',
              },
              {
                label: 'Company Info',
                icon: <Info className="w-5 h-5" />,
                route: '/admin/companyinfo',
                requiredPermission: 'Settings.CompanyInfo.View',
              },
              {
                label: 'Example Parent',
                icon: <List className="w-5 h-5" />,
                children: [
                  {
                    label: 'Example Child 1',
                    route: '/admin/sometest',
                    requiredPermission: 'Settings.CompanyInfo.View',
                  },
                ],
              },
              {
                label: 'Survey Invites',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/surveyinvites',
                requiredPermission: 'Settings.SurveyInvite.View',
              },
              {
                label: 'Site Survey Responses',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/sitesurveyresponses',
                requiredPermission: 'Settings.SiteSurveyResponse.View',
              },
              {
                label: 'Users',
                icon: <Users className="w-5 h-5" />,
                route: '/admin/users',
                requiredPermission: 'Settings.Administrators.View',
              },
            ],
          },
        ]
      : []),
  ], [roles]);

  const filteredGroups = useMemo(
    () =>
      menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (!item.requiredPermission ||
                permissions.includes(item.requiredPermission)) &&
              item.label.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [menuGroups, permissions, searchTerm],
  );

  const handleScroll = useCallback((e: any) => {
    if (scrollContainerRef.current?._ps) {
      localStorage.setItem(
        'sidebarScrollPosition',
        scrollContainerRef.current._ps.element.scrollTop.toString(),
      );
    }
  }, []);

  useLayoutEffect(() => {
    if (!activePage) return;

    const immediateScroll = () => {
      if (scrollContainerRef.current?._ps) {
        const savedPosition = localStorage.getItem('sidebarScrollPosition');
        const container = scrollContainerRef.current._ps.element;

        if (savedPosition) {
          container.scrollTop = parseInt(savedPosition, 10);
        } else {
          const activeElement = document.querySelector(
            `[data-route="${activePage}"]`,
          ) as HTMLElement;

          if (activeElement) {
            const containerHeight = container.clientHeight;
            const elementTop = activeElement.offsetTop;
            container.scrollTop = elementTop - containerHeight / 2;
          }
        }
        scrollContainerRef.current._ps.update();
      }
    };

    immediateScroll();
    const timer = setTimeout(immediateScroll, 0);

    return () => clearTimeout(timer);
  }, [activePage]);

  return (
    <aside
      id="nav-menu"
      aria-label="Side navigation"
      className={`fixed lg:static z-40 w-72 bg-charcoal border-r border-seam h-[calc(100%-4rem)] lg:h-full transition-transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 mt-12 lg:mt-0 flex flex-col`}
    >
      {/* Search Box */}
      <div className="relative my-6 px-4">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search here"
          aria-label="Search content"
          className="relative w-full h-10 px-4 pr-12 text-sm transition-all border rounded outline-none
            border-seam bg-graphite text-harbor autofill:bg-graphite
            focus:border-secondary focus:outline-none
            disabled:cursor-not-allowed disabled:bg-graphite disabled:text-gray-500"
        />
        <Search
          className="absolute top-2.5 right-7 h-5 w-5 cursor-pointer stroke-harbor"
          aria-hidden="true"
          aria-label="Search icon"
          role="graphics-symbol"
        />
      </div>

      {/* Navigation Links */}
      <PerfectScrollbar
        ref={scrollContainerRef}
        options={{ suppressScrollX: true }}
        className="flex-1 overflow-hidden"
        onScrollY={handleScroll}
      >
        <div className="">
          {filteredGroups.map((group, index) => (
            <div key={index} className="mb-4">
              <h2 className="px-4 py-2 text-xs font-semibold text-harbor uppercase">
                {group.group}
              </h2>
              <ul className="space-y-2 px-4">{renderMenuItems(group.items)}</ul>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
      {adminId && (
        <SimpleAlert message="Logged in as member" variant="success" />
      )}

      {/* Footer */}
      <footer className="border-t border-seam p-4 mt-auto">
        <p className="text-sm text-harbor">
          &copy; {new Date().getFullYear()} Natshoot. All rights reserved.
          Developed by Latreia.
        </p>
      </footer>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
